import { DragSide } from "./geometry";
import { WithOptional } from "./utils";
import * as typ from "./types";
import { dev } from "@utils/dev";

type MatrixItem = typ.Item;

interface ItemWantsPos extends MatrixItem {
  wantedCol: number;
}

const doesItemWantPos = (item: MatrixItem): item is ItemWantsPos => (item as ItemWantsPos).wantedCol !== undefined;
const wantedPos = (it: MatrixItem): number => doesItemWantPos(it) ? it.wantedCol : it.pos.col;
const sortItems = (items: MatrixItem[]) =>
  items.sort((a, b) => {
    const wa = wantedPos(a), wb = wantedPos(b);
    if (wa === wb) {
      if (doesItemWantPos(a)) return -1;
      return 1;
    }
    return wa - wb;
  });

type Row_ = typ.Row;

export interface Row extends Row_ {
  normal: boolean;
}

export const isNormalRow = (r: Row_): r is Row => (r as Row).normal;

type Matrix_ = typ.Matrix;

export interface Matrix extends Matrix_ {
  normal: boolean;
}

export const isNormalMatrix = (m: Matrix_): m is Matrix => (m as Matrix).normal && m.rows.filter(r => !isNormalRow(r)).length === 0;

type RowMaker = (rowOffset: number) => Row_;
type MatrixMaker = () => Matrix_;

const normalRow = (r: Row_): Row => ({...r, normal: true})
const normalMatrix = (m: Matrix_): Matrix => ({...m, normal: true})

export const debugPrintRow = (r: Row_) => {
  if (!r) {
    dev.llog("  EMPTY ABNORMAL ROW!");
    return;
  }
  dev.llog("  ROW START")  
  for (let i = 0; i < r.items.length; i++) {
    const item = r.items[i];
    if (!item) continue;
    dev.llog(`    ITEM ID:${item.id},col:${item.pos.col},width:${item.numCols},pos:${i}`);
  }
  dev.llog("  ROW END");
}

export const debugPrintMatrix = (m: Matrix_, pref = "") => {
  dev.llog("\nSTART MATRIX " + pref);
  for (const r of m.rows){
    debugPrintRow(r);
  }
  dev.llog("END MATRIX");
}

const copyRow = (row: Row_): Row_ => ({ ...row, items: {...row.items} });

export const copyMatrixAndCheckIsNormal = (m: Matrix_) => {
  if (!isNormalMatrix(m)) throw Error("[copyMatrixAndCheckIsNormal] error: this is not a normal matrix");
  return {
    ...m,
    rows: m.rows.map(r => copyRow(r) as Row),
  }
}

const isRowEmpty = (r: Row_) => { // принимает как нормальные так и ненормальные строки
  return !r.items.filter(x => !!x).length;
}

const hasRepeats = (r: Row_): boolean => {
  const tab: Set<string> = new Set;
  for (const item of r.items) {
    if (!item) continue;
    if (tab.has(item.id)) return true;
    tab.add(item.id);
  }
  return false;
}

class MatrixAlgo {

  constructor(private rowMaker_: RowMaker) {}

  private normalizeRow_(r_: Row_ | undefined, rowOffset: number): Row { // для ненормальных строк
    if (!r_) return normalRow(this.rowMaker_(rowOffset));

    if (hasRepeats(r_)) dev.log("[MatrixAlgo.normalizeRow_] REPEATS AT START");

    const r = {...r_, normal: false} as Row;
    r.offset = rowOffset;
    r.length = 0;

    const newItems: MatrixItem[] = [];

    const setItem = (it: MatrixItem, offs: number): number => {
      it.pos = {col: offs, row: rowOffset};
      if (doesItemWantPos(it)) {
        delete (it as WithOptional<ItemWantsPos, "wantedCol">).wantedCol;
      }
      newItems[offs] = it;
      return it.numCols;
    };

    const items = r.items.filter(it => !!it);
    sortItems(items);

    let len = 0;
    for (const item of items) {
      const col = wantedPos(item);
      setItem(item, col < len ? len : col);
      len = item.pos.col + item.numCols;
    }

    r.items = newItems;
    r.length = len;
    r.normal = true;

    if (hasRepeats(r)) dev.log("[MatrixAlgo.normalizeRow_] REPEATS AT END");
    return r;
  }

  private alignRow_(r_: Row_ | undefined, rowOffset: number): Row { // для ненормальных строк
    if (!r_) return normalRow(this.rowMaker_(rowOffset));

    if (hasRepeats(r_)) dev.log("[MatrixAlgo.alignRow_] REPEATS AT START");

    const r = {...r_, normal: false} as Row;
    r.offset = rowOffset;
    r.length = 0;

    const newItems: MatrixItem[] = [];

    const setItem = (it: MatrixItem, offs: number): number => {
      it.pos = {col: offs, row: rowOffset};
      if (doesItemWantPos(it)) {
        delete (it as WithOptional<ItemWantsPos, "wantedCol">).wantedCol;
      }
      newItems[offs] = it;
      return it.numCols;
    };

    const items = r.items.filter(it => !!it);
    sortItems(items);

    let len = 0;
    let pos = 0;
    for (const item of items) {
      const col = pos;
      setItem(item, col < len ? len : col);
      len = item.pos.col + item.numCols;
      pos += item.numCols;
    }

    r.items = newItems;
    r.length = len;
    r.normal = true;

    if (hasRepeats(r)) dev.log("[MatrixAlgo.alignRow_] REPEATS AT END");
    return r;
  }

  private checkIsNormalRow_(r: Row_): r is Row {
    let len = 0;
    for (let i = 0; i < r.items.length; i++) {
      const item = r.items[i];
      if (!item) continue;
      if (item.pos.row !== r.offset) return false;
      if (item.pos.col !== i) return false;
      if (item.pos.col < len) return false;
      len = item.pos.col + item.numCols;
    }
    if (r.length !== len) return false;
    (r as Row).normal = true;
    return true;
  }

  normalizeMatrix(m_: Matrix_): Matrix {
    //debugPrintMatrix(m_, "BEFORE");
    const m: Matrix = {...(m_ as Matrix), normal: false, idToItem: {}};
    if (!m.rows.length) m.rows.push(this.rowMaker_(0));

    for (let i = 0; i < m.rows.length; i++) {
      m.rows[i] = this.normalizeRow_(m.rows[i], i);
      for (const item of m.rows[i].items) if (item) m.idToItem[item.id] = item;
    }

    for (let i = m.rows.length - 1; i > 0; i--) { // хотя бы одна строка должна остаться
      if (isRowEmpty(m.rows[i])) m.rows.pop();
      else break;
    }

    m.normal = true;
    //debugPrintMatrix(m, "AFTER");
    return m;
  }

  alignMatrix(m_: Matrix_): Matrix {
    //debugPrintMatrix(m_, "BEFORE");
    const m: Matrix = {...(m_ as Matrix), normal: false, idToItem: {}};
    if (!m.rows.length) m.rows.push(this.rowMaker_(0));

    for (let i = 0; i < m.rows.length; i++) {
      m.rows[i] = this.alignRow_(m.rows[i], i);
      for (const item of m.rows[i].items) if (item) m.idToItem[item.id] = item;
    }

    for (let i = m.rows.length - 1; i > 0; i--) { // хотя бы одна строка должна остаться
      if (isRowEmpty(m.rows[i])) m.rows.pop();
      else break;
    }

    m.normal = true;
    //debugPrintMatrix(m, "AFTER");
    return m;
  }

  checkIsNormalMatrix(m: Matrix_): m is Matrix {
    if (m.rows.length < 1) return false;
    let nempty = 0;
    for (let i = 0; i < m.rows.length; i++) {
      const r = m.rows[i];
      if (r.offset !== i) return false;
      if (!this.checkIsNormalRow_(r)) return false;
      if (isRowEmpty(r)) nempty++
      else nempty = 0;
      for (const item of r.items) {
        if (m.idToItem[item.id] !== item) return false;
      }
    }
    for (const [id, item] of Object.entries(m.idToItem)) {
      if (item.id !== id) return false;
      if (!m.rows[item.pos.row].items.find(it => it && (it.id === id))) return false;
    }
    if (nempty > 1) return false;
    (m as Matrix).normal = true;
    return true;
  }

  private dryRow_(r: Row): Row_ { // отменяет нормализацию строки
    r.items = r.items.filter(it => !!it);
    r.items.sort((a, b) => a.pos.col - b.pos.col);
    r.normal = false;
    return r;
  }

  private removeItem_(r: Row_, item: MatrixItem) {
    const len = r.items.length;
    r.items = r.items.filter(it => it.id !== item.id);
    if (r.items.length < len) (r as Row).normal = false;
  }

  private insertItem_(r: Row_, item: MatrixItem, col: number, after: boolean) {
    item.pos.row = r.offset;
    const thatItem = r.items.find(it => it && (it.pos.col <= col && it.pos.col + it.numCols > col));
    const wIt = item as ItemWantsPos;
    if (thatItem) {
      if (after) wIt.wantedCol = thatItem.pos.col + thatItem.numCols;
      else wIt.wantedCol = thatItem.pos.col;
    } else wIt.wantedCol = col;
    r.items.push(item);
    sortItems(r.items);
  }

  private exchangeHorz_(m: Matrix_, src: MatrixItem, dst: MatrixItem) {
    //console.log("EXCHANGE!");
    const ra = m.rows[src.pos.row];
    const rb = m.rows[dst.pos.row];
    if (!ra || !rb) throw Error("[MatrixAlgo.exchangeHorz_] error: row does not exist");
    const indSrc = ra.items.findIndex(it => it === src);
    const indB = rb.items.findIndex(it => it === dst);
    if (indSrc < 0 || indB < 0) throw Error("[MatrixAlgo.exchangeHorz_] error: item does not exist in this row");
    const s = src as ItemWantsPos;
    const d = dst as ItemWantsPos;
    s.wantedCol = d.pos.col;
    d.wantedCol = s.pos.col;
    ra.items[indSrc] = dst;
    rb.items[indB] = src;
  }

  moveItem(m: Matrix_, srcID: string, dstPos: typ.ItemPos, side: DragSide, destID: string): Matrix {
    const src = m.idToItem[srcID];
    if (!src) throw Error("[MatrixAlgo.moveItem] error: item not found");

    (m as Matrix).normal = false;

    const vertical = [DragSide.top, DragSide.bottom].includes(side);
    const before = [DragSide.left, DragSide.top].includes(side);
    const dst = m.idToItem[destID];
    if (dst) dstPos = dst.pos;

    // проверяем, если вставка на пустое место то делаем ее просто
    if (!dst) {
      //console.log("MOVE A");
      this.removeItem_(m.rows[src.pos.row], src);
      this.insertItem_(m.rows[dstPos.row], src, dstPos.col, false);
      return this.normalizeMatrix(m);
    }

    if (!vertical) {
      //console.log("MOVE C");
      const sameRow = src.pos.row === dst.pos.row 
      
      const doExchange =
        (src.pos.col + src.numCols === dst.pos.col && side === DragSide.right) || 
        (dstPos.col + dst.numCols === src.pos.col && side === DragSide.left)
      ;

      if (sameRow && doExchange) {
        //console.log("MOVE D");
        this.exchangeHorz_(m, src, dst);
        return this.normalizeMatrix(m);
      }

      //console.log("MOVE F");
      this.removeItem_(m.rows[src.pos.row], src);
      this.insertItem_(m.rows[dstPos.row], src, dstPos.col, !before);
      return this.normalizeMatrix(m);
    }

    //console.log("MOVE G");
  
    dstPos.row = before ? dstPos.row - 1 : dstPos.row + 1;
    if (dstPos.row < 0) dstPos.row = 0;
    if (!m.rows[dstPos.row]) m.rows[dstPos.row] = this.rowMaker_(dstPos.row);
    this.removeItem_(m.rows[src.pos.row], src);
    this.insertItem_(m.rows[dstPos.row], src, dstPos.col, false);
    return this.normalizeMatrix(m);
  }
}

export interface ReorderInfo {
  id: string;
  pos: typ.ItemPos;
}

export class MatrixOps {
  private algo_: MatrixAlgo;
  private data_: Matrix_;

  constructor(matrixMaker_: MatrixMaker, private rowMaker_: RowMaker) {
    this.algo_ = new MatrixAlgo(rowMaker_);
    this.data_ = matrixMaker_()
  };


  withData(data: Matrix_): MatrixOps {
    this.data_ = data;
    return this;
  }

  get data(): Matrix {
    return copyMatrixAndCheckIsNormal(this.data_);
  }

  set(item: MatrixItem, normalize: boolean, row?: number, col?: number) {
    //console.trace("MATRIX SET");
    try {
      if (row === undefined) row = item.pos.row;
      else item.pos.row = row;
      if (col === undefined) col = item.pos.col;
      else item.pos.col = col;

      if (!this.data_.rows[row]) this.data_.rows[row] = this.rowMaker_(row);
      this.data_.rows[row].items.push(item);
      // после этого нужна нормализация
      if (normalize) this.normalize();
    } catch (e) {
      dev.err("[MatrixOps.set] error:", e);
      throw e;
    }
  }

  private _fill(item: WithOptional<MatrixItem, "pos">) {
    const row = 0;
    if (!this.data_.rows[row]) this.data_.rows[row] = this.rowMaker_(row);
    const r = this.data_.rows[row]; 
    let col: number = 0;
    if (!isRowEmpty(r)) {
      let prevCol = 0;
      let colAfter = 0;
      let found = false;
      for (let i = 0; i < r.items.length; i++) {
        const it = r.items[i];
        if (!it) continue;
        if (it.pos.col - prevCol >= item.numCols) {
          col = colAfter;
          found = true;
          break;
        }
        prevCol = it.pos.col;
        colAfter = it.pos.col + it.numCols;
      }
      if (!found) {
        col = colAfter;
      }
    }
    r.items.push({...item, pos: {row, col}});
    // после этого нужна нормализация
  }

  private _drop(item: WithOptional<MatrixItem, "pos">, pos: typ.ItemPos, side?: DragSide) {
    let row = pos.row;
    if (!this.data_.rows[row]) this.data_.rows[row] = this.rowMaker_(row);
    let r = this.data_.rows[row]; 
    let col = pos.col;

    let thatItem = r.items.find(it => it && (it.pos.col <= pos.col && pos.col < it.pos.col + it.numCols));
    if (!thatItem) {
      r.items.push({...item, pos});
      return;
    }

    switch (side) {
      case DragSide.right:
        col++;
        break;
      case DragSide.bottom:
        row++;
        if (!this.data_.rows[row]) this.data_.rows[row] = this.rowMaker_(row);
        r = this.data_.rows[row]; 
        break;
    }

    thatItem = r.items.find(it => it && (it.pos.col <= pos.col && pos.col < it.pos.col + it.numCols));
    const newItem = {...item, pos: {row, col}};

    if (thatItem) {
      (newItem as ItemWantsPos).wantedCol = col;
    }

    r.items.push(newItem);
  }

  normalize(): Matrix { 
    //console.trace("MATRIX NORMALIZE");
    try {
      const data = this.algo_.normalizeMatrix(this.data_);
      this.data_ = data;
      return data;
    } catch (e) {
      dev.err("[MatrixOps.normalize] error:", e);
      throw e;
    }
  }

  align(): [Matrix, ReorderInfo[] | undefined] { 
    //console.trace("MATRIX NORMALIZE");
    try {
      const reorder = this.withReorderControl(() => {
        const data = this.algo_.alignMatrix(this.data_);
        this.data_ = data;
      })
      return [this.data_ as Matrix, reorder];
    } catch (e) {
      dev.err("[MatrixOps.align] error:", e);
      throw e;
    }
  }

  add(item: WithOptional<MatrixItem, "pos">, pos?: typ.ItemPos, side?: DragSide) {
    try {
      if (pos) this._drop(item, pos, side);
      else this._fill(item);
      this.normalize();
    } catch (e) {
      dev.err("[MatrixOps.add] error:", e);
      throw e;
    }
  }

  row(row: number): Row { 
    const r = this.data_.rows[row];
    if (isNormalRow(r)) return r;
    throw Error("[MatrixOps.row] error: abnormal row");
  }

  private makePosTab_(): Record<string, typ.ItemPos> {
    const tab: Record<string, typ.ItemPos> = {};
    for (const r of this.data_.rows) {
      for (const item of r.items) if (item) {
        tab[item.id] = {...item.pos};
      }
    }
    return tab;
  }

  withReorderControl(f: () => void): ReorderInfo[] | undefined {
    const tab = this.makePosTab_();
    try {
      f();
    } catch (e) {
      dev.err("[MatrixOps.withReorderControl] error:", e);
      throw e;
    } finally {
      const newt = this.makePosTab_();
      const res: ReorderInfo[] = [];
      for (const id in newt) {
        const pos = newt[id];
        if (!tab.hasOwnProperty(id)) res.push({id, pos});
        else {
          const npos = tab[id];
          if (npos.col !== pos.col || npos.row !== pos.row) res.push({id, pos});
        }
      }
      return res.length ? res : undefined;
    }
  }

  move(srcID: string, destPos: typ.ItemPos, side: DragSide, dest: string): [Matrix, ReorderInfo[] | undefined] {
    //console.trace("MATRIX MOVE", dest);
    let data: Matrix = this.data_ as Matrix;
    try {
      const reorder = this.withReorderControl(() => {
        data = this.algo_.moveItem(this.data_, srcID, destPos, side, dest);
        this.data_ = data;
      });
      return [data, reorder];
    } catch (e) {
      dev.err("[MatrixOps.move] error:", e);
      throw e;
    }
  }

  at(row: number, col: number): MatrixItem | undefined {
    try {
      const r = this.data_.rows[row];
      if (!r) return undefined;
      return r.items.find(it => it && (it.pos.col <= col && col < it.pos.col + it.numCols));
    } catch (e) {
      dev.err("[MatrixOps.at] error:", e);
      throw e;
    }
  }

  get nrows() { return this.data_.rows.length; }
}